import { useContext, useEffect, useMemo, useState } from "react";
import { Box, HStack, Text } from "@chakra-ui/react";
import { Field, FormInput, FormSelect, FormSwitch, FormTextarea } from "../ui/inputs";
import defaultLabels from "../labels.default";
import {
    MISSING_POLICY,
    SCORING_TYPE,
    createEmptyScoringConfig,
} from "../models/scoringModel";
import ValueRulesEditor from "./rules/ValueRulesEditor";
import RangeRulesEditor from "./rules/RangeRulesEditor";
import SetRulesEditor from "./rules/SetRulesEditor";
import AiMatchRulesEditor from "./rules/AiMatchRulesEditor";
import InheritanceBadge from "./InheritanceBadge";
import ScoringValidationContext from "../validation/ScoringValidationContext";
import { scoringConfigSchema } from "../validation/scoringSchema";

const RULE_GROUP_LABELS = { valueRules: "reguła wartości", rangeRules: "reguła zakresu", setRules: "reguła zbioru", aiLevels: "poziom AI" };

const describeProblems = (cfg) => {
    try {
        scoringConfigSchema.validateSync(cfg, { context: { maxScore: cfg.maxScore }, abortEarly: false });
        return [];
    } catch (err) {
        return (err.inner?.length ? err.inner : [{ message: err.message, path: "" }]).map((e) => {
            const m = /^(\w+)\[(\d+)\]/.exec(e.path ?? "");
            if (!m) return e.message;
            const points = cfg[m[1]]?.[Number(m[2])]?.points;
            const extra = points != null && e.message.includes("przekracza") ? ` (${points} pkt przy maks. ${cfg.maxScore} pkt)` : "";
            return `${RULE_GROUP_LABELS[m[1]] ?? m[1]} nr ${Number(m[2]) + 1}: ${e.message}${extra}`;
        });
    }
};

/**
 * Single collapsible "Ocena / Scoring" section for one criterion.
 *
 * UX contract (binding, from product): every scoring-related setting for a
 * criterion lives in exactly ONE section, visually separated from the rest
 * of the criterion's fields (question text, collection config, etc.).
 * - Header bar (always visible, even collapsed): ON/OFF switch + a short
 *   summary ("Ocena: włączona", "Max: 15 pkt", or inheritance info) +
 *   click-to-expand/collapse.
 * - Body (only when expanded): all fields (maxScore, missingPolicy,
 *   scoringType, type-specific rules, description) are always rendered AND
 *   editable regardless of the scoringEnabled toggle, so the structure can
 *   be fully defined ahead of turning scoring on. Only `readOnly` (LOCKED,
 *   inherited criterion) disables them -- the toggle only gates whether
 *   scoring is *applied*, not whether it can be *defined*.
 *
 * Same component, same fields, same behavior in both the admin scenario
 * creator and the client recruitment form -- only `inheritance`/`readOnly`
 * differ between hosts, never the layout or field set.
 *
 * @param {object} props
 * @param {import("../models/scoringModel").ScoringConfig} props.config
 * @param {(patch: Partial<import("../models/scoringModel").ScoringConfig>) => void} props.onChange
 * @param {boolean} [props.readOnly] - true when this criterion is LOCKED (inherited, not editable here)
 * @param {'local'|'locked'|'overridable'|'overridden'} [props.inheritanceState]
 * @param {() => void} [props.onCustomizeForRecruitment] - called when the user clicks "Dostosuj dla tej rekrutacji"
 * @param {object} [props.labels] - label overrides; defaults to Polish (see labels.default.js)
 */
const ScoringConfigPanel = ({
    config,
    onChange,
    readOnly = false,
    inheritanceState,
    onCustomizeForRecruitment,
    labels: labelOverrides,
}) => {
    const labels = { ...defaultLabels, ...labelOverrides };
    const [open, setOpen] = useState(false);
    const cfg = config ?? createEmptyScoringConfig();
    const showValidation = useContext(ScoringValidationContext);
    const problems = useMemo(() => (showValidation ? describeProblems(cfg) : []), [showValidation, cfg]);
    const hasProblems = problems.length > 0;

    useEffect(() => {
        if (hasProblems) setOpen(true);
    }, [showValidation]); // eslint-disable-line react-hooks/exhaustive-deps

    const patch = (updates) => onChange({ ...cfg, ...updates });

    const missingPolicyOptions = Object.values(MISSING_POLICY).map((v) => ({
        value: v,
        label: labels.missingPolicyOptions[v],
    }));
    const scoringTypeOptions = Object.values(SCORING_TYPE).map((v) => ({
        value: v,
        label: labels.scoringTypeOptions[v],
    }));

    const summary = cfg.scoringEnabled
        ? [labels.summaryEnabled, cfg.maxScore != null ? labels.summaryMax(cfg.maxScore) : null].filter(Boolean).join(" · ")
        : labels.summaryDisabled;

    // Only `readOnly` (LOCKED, inherited criterion) disables the fields -- the
    // scoringEnabled toggle only gates whether scoring is *applied*, not
    // whether it can be *defined*, so the user can prepare rules ahead of
    // switching scoring on.
    const fieldsDisabled = readOnly;

    return (
        <Box
            w="full"
            borderWidth={hasProblems ? 2 : 1}
            borderColor={hasProblems ? "red.500" : "gray.200"}
            borderRadius={8}
            overflow="hidden"
            bg="white"
            data-scoring-invalid={hasProblems ? "true" : undefined}
        >
            <HStack
                px={4}
                py={2.5}
                justify="space-between"
                bg="gray.50"
                cursor="pointer"
                onClick={() => setOpen((o) => !o)}
            >
                <HStack gap={3}>
                    <Box onClick={(e) => e.stopPropagation()}>
                        <FormSwitch
                            checked={!!cfg.scoringEnabled}
                            onChange={(v) => {
                                if (readOnly) return;
                                patch({ scoringEnabled: v });
                                if (v) setOpen(true);
                            }}
                            disabled={readOnly}
                            size="sm"
                        />
                    </Box>
                    <Text fontSize="sm" fontWeight={600} color="gray.900">{labels.sectionTitle}</Text>
                    <Text fontSize="xs" color="gray.500">{summary}</Text>
                </HStack>
                <Text fontSize="xs" color="gray.400" transform={open ? "rotate(180deg)" : "none"} transition="transform 0.2s">
                    ▼
                </Text>
            </HStack>

            {hasProblems && (
                <Box px={4} py={2} bg="red.50" borderTop="1px solid" borderColor="red.200">
                    {problems.map((msg) => (
                        <Text key={msg} fontSize="xs" color="red.700">• {msg}</Text>
                    ))}
                </Box>
            )}

            {open && (
                <Box p={4} display="flex" flexDirection="column" gap={4} borderTop="1px solid" borderColor="gray.100">
                    {inheritanceState && (
                        <InheritanceBadge
                            state={inheritanceState}
                            onCustomizeForRecruitment={onCustomizeForRecruitment}
                            labels={labels}
                        />
                    )}

                    <HStack gap={3} align="flex-end">
                        <Box flex={1}>
                            <Field label={labels.maxScore}>
                                <FormInput
                                    type="number"
                                    min="0"
                                    disabled={fieldsDisabled}
                                    value={cfg.maxScore ?? ""}
                                    onChange={(e) => patch({ maxScore: e.target.value === "" ? null : Number(e.target.value) })}
                                />
                            </Field>
                        </Box>
                        <Box flex={1}>
                            <Field label={labels.missingPolicy}>
                                <FormSelect
                                    value={cfg.missingPolicy}
                                    onChange={(v) => patch({ missingPolicy: v })}
                                    options={missingPolicyOptions}
                                    disabled={fieldsDisabled}
                                />
                            </Field>
                        </Box>
                        <Box flex={1}>
                            <Field label={labels.scoringType}>
                                <FormSelect
                                    value={cfg.scoringType}
                                    onChange={(v) => patch({ scoringType: v })}
                                    options={scoringTypeOptions}
                                    disabled={fieldsDisabled}
                                />
                            </Field>
                        </Box>
                    </HStack>

                    <fieldset disabled={fieldsDisabled} style={{ border: "none", padding: 0, margin: 0 }}>
                        {cfg.scoringType === SCORING_TYPE.VALUE && (
                            <ValueRulesEditor rules={cfg.valueRules} maxScore={cfg.maxScore} onChange={(valueRules) => patch({ valueRules })} labels={labels} disabled={fieldsDisabled} />
                        )}
                        {cfg.scoringType === SCORING_TYPE.RANGE && (
                            <RangeRulesEditor rules={cfg.rangeRules} maxScore={cfg.maxScore} onChange={(rangeRules) => patch({ rangeRules })} labels={labels} disabled={fieldsDisabled} />
                        )}
                        {cfg.scoringType === SCORING_TYPE.SET && (
                            <SetRulesEditor config={cfg} onChange={patch} labels={labels} disabled={fieldsDisabled} />
                        )}
                        {cfg.scoringType === SCORING_TYPE.AI_MATCH && (
                            <AiMatchRulesEditor config={cfg} onChange={patch} labels={labels} disabled={fieldsDisabled} />
                        )}
                    </fieldset>

                    <Field label={labels.description}>
                        <FormTextarea
                            rows={2}
                            disabled={fieldsDisabled}
                            value={cfg.description ?? ""}
                            onChange={(e) => patch({ description: e.target.value })}
                        />
                    </Field>
                </Box>
            )}
        </Box>
    );
};

export default ScoringConfigPanel;
